/**
 * Splitting an upload into one Submission per student — ONE JOB PER UPLOAD, and the only `heavy` type.
 *
 * WHAT IT REPLACES. This ran inline in the upload request, which made it the last piece of setup with none of
 * what every other piece has: no lease, so a restart lost it with no trace but a `STARTED` progress record and
 * zero submissions; no retry, so a transient split-agent failure was reported to the teacher as final; and no
 * place in the fairness accounting, so two teachers uploading at once simply competed.
 *
 * NOT A FAN-OUT, unlike `answerExtraction`. The split is one call over all pages to decide where one student's
 * work ends and the next begins — the boundaries are the output, so there is nothing to partition beforehand.
 * That makes it shaped like `structure`: one row, exam-scoped lock, its own progress record.
 *
 * `heavy`, AND IT IS THE FIRST TYPE TO SAY SO. Every other type left `pool` unset and therefore ran as `io`,
 * which meant `HEAVY_ADMISSIONS_PER_TICK` and the pool-scoped half of `cpuHeadroomCheck` had nothing to act on.
 * This type is what they were written for: after the split agent returns, every student block goes through
 * `persistFileAsImageIds`, which explodes a PDF into page images and encodes each one in the encode worker.
 * That is core-bound, it is the work `load.ts` describes as spawning ten pipelines, and admitting a second one
 * against a saturated loop is how the instance stops serving anything else. One admission per tick, and only
 * with CPU headroom.
 */

import { runSubmissionSplit } from '../../submission-split.js';
import { uploadLockKey } from '../job.js';
import { defineJob } from '../registry.js';

export interface SubmissionSplitPayload {
  /** The persisted uploads to split. Ids rather than files — that is what made this expressible as a row. */
  fileIds: string[];
  /** Where the confirmation request opens. Absent for an upload with no conversation behind it. */
  conversationId?: string;
  guestUserID?: string;
  /**
   * Who the created submissions belong to, resolved AT DISPATCH — the last point where a session exists. A
   * handler cannot ask: `CurrentUserInfo.getMyself()` throws outside a request, and even if it returned null
   * the answer would be wrong rather than absent.
   */
  ownerUserID?: string;
  [key: string]: unknown;
}

export const submissionSplitJob = defineJob<SubmissionSplitPayload>({
  type: 'submissionSplit',

  pool: 'heavy',

  /**
   * The exam's upload, not its task tree. The split writes Submission documents and the upload/split-confirmation
   * progress and touches `tasks` not at all, so it can run beside a structure extraction or a solution step —
   * which matters, because on a first upload it does.
   *
   * Exclusive per exam all the same: two concurrent splits of one exam would both create a full cohort.
   */
  /**
   * One row, and NO RUN TO OPEN — the only batch here without one. The split's progress is `uploadProgress` and
   * `splitConfirmationProgress`, both of which the work itself writes as it goes: there is no per-unit set to seed,
   * because the units (the students) are what the split has to discover.
   */
  batch: {
    units: (_examId, scope) => {
      const { fileIds = [], ...owners } = scope as {
        fileIds?: string[];
        conversationId?: string;
        guestUserID?: string;
        ownerUserID?: string;
      };
      // No files is no work, not an empty row — the split's `ready()` would drop it, but a row that exists only to
      // be dropped still shows up in the queue and in the exam's job list.
      return fileIds.length ? [{ fileIds, ...owners }] : [];
    },
  },

  lockKey: examId => uploadLockKey(examId),

  /**
   * The SET of files, order-independent. Two dispatches naming the same upload are the same work, so the second
   * joins the first instead of doubling the cohort; a genuinely different upload has different ids and gets its
   * own row. Sorted because the caller's order is an accident of how the files were resolved.
   */
  dedupeKey: (examId, payload) => `submissionSplit:${examId}:${[...payload.fileIds].sort().join(',')}`,

  ready: (_exam, job) => {
    if (job.payload.fileIds.length === 0) return { verdict: 'drop', because: 'submissionSplit:no-files' };
    // Deliberately NOT gated on a confirmed structure. The split is what produces the submissions the structure
    // step reads as its own source (`hasSubmissions`), so gating it on the structure would deadlock the two.
    return 'run';
  },

  /**
   * The split agent over every page, then a PDF explode and encode per student. Generous because it scales with
   * the cohort rather than with one unit, and losing a lease mid-encode costs the whole upload.
   */
  maxRuntimeMs: 30 * 60_000,

  /**
   * Retried, which the inline version could not be — and safe to retry only because `runSubmissionSplit` deletes
   * the submissions its own previous attempt created. Without that tag a half-finished attempt plus a retry
   * would silently produce a cohort and a half.
   */
  maxAttempts: 3,

  handler: async (examId, payload, context) => {
    const submissionIds = await runSubmissionSplit(examId, {
      fileIds: payload.fileIds,
      ...(payload.conversationId ? { conversationId: payload.conversationId } : {}),
      ...(payload.guestUserID ? { guestUserID: payload.guestUserID } : {}),
      ...(payload.ownerUserID ? { ownerUserID: payload.ownerUserID } : {}),
      // The ROW's id, so a retry of this row — and only this row — can clean up after itself.
      splitJobId: context.job._id,
    });

    if (submissionIds.length === 0) return { skipped: true, note: 'split produced no submissions' };
    return { note: `split created ${submissionIds.length} submissions` };
  },
});
